import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { StyleSheet, View } from 'react-native'
import Animated, {
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'

import { ActionLoading } from '../components/ActionLoading'
import { useDepositActionPhase } from '../components/DepositActionMotion'
import type { PillButtonSize } from '../components/PillButton'
import { buttonRadius } from '../theme/buttonRadius'
import { useTheme } from '../theme/restyle'
import { MOTION_DURATION, MOTION_TIMING } from '../utils/motion'

// Do not block payment indefinitely if a provider never calls onReady.
const READY_CEILING_MS = 4000

// Keep the provider mounted while its loading state covers it. Hiding a WebView
// can prevent iOS from loading it until it becomes visible.
export function SurfaceSlot({
  renderSurface,
  surfaceKey,
  label,
  size,
  covered = false,
  onReadyChange,
  onReadyTimeout,
  readyCeilingMs = READY_CEILING_MS,
}: {
  /** Announced while the cover is up; the design draws no copy on it. */
  label: string
  size: PillButtonSize
  /**
   * Puts the cover back over a ready surface — a payment that has gone
   * through but not yet landed. The provider stays mounted underneath, so it
   * can still report what happens to it.
   */
  covered?: boolean
  /**
   * Whether the provider's button is the one on screen, for chrome outside
   * the slot that has to land with it. A slot on its way out reports ready:
   * what is gone holds nothing back.
   */
  onReadyChange?: (ready: boolean) => void
  /** Ends an unresolved provider attempt instead of exposing its empty slot. */
  onReadyTimeout?: () => void
  /** Override for provider probes that intentionally stay covered while they resolve. */
  readyCeilingMs?: number
} & (
  | {
      renderSurface: (onReady: () => void) => ReactNode
      /** Unique per quote or retry so readiness cannot carry into another attempt. */
      surfaceKey: string
    }
  | { renderSurface?: undefined; surfaceKey?: undefined }
)) {
  const theme = useTheme()
  const reduceMotion = useReducedMotion()
  const phase = useDepositActionPhase()
  const height = theme.sizes[size]
  const radius = buttonRadius(theme, height)
  const activeKey = renderSurface === undefined ? undefined : surfaceKey
  const [readyFor, setReadyFor] = useState<string>()
  const [uncoveredFor, setUncoveredFor] = useState<string>()
  const ready = activeKey !== undefined && readyFor === activeKey && !covered
  const coverOpacity = useSharedValue(1)

  useLayoutEffect(() => {
    if (activeKey === undefined) {
      setReadyFor(undefined)
    }
  }, [activeKey])

  // Before paint: a passive effect would let the screen draw one frame with
  // the cover up and the chrome beside it already settled.
  useLayoutEffect(() => {
    onReadyChange?.(ready)
    return () => onReadyChange?.(true)
  }, [ready, onReadyChange])

  // The provider's control is already painted underneath, so the cover
  // dissolves onto it instead of cutting away and blinking the slot.
  useEffect(() => {
    if (!ready) {
      coverOpacity.value = 1
      setUncoveredFor(undefined)
      return
    }
    if (phase !== null && phase.controlFill !== null) {
      phase.fillColor = phase.controlFill
    }
    if (reduceMotion) {
      setUncoveredFor(activeKey)
      return
    }
    coverOpacity.value = withTiming(0, MOTION_TIMING)
    const settled = setTimeout(
      () => setUncoveredFor(activeKey),
      MOTION_DURATION,
    )
    return () => clearTimeout(settled)
  }, [ready, activeKey, reduceMotion, coverOpacity, phase])

  const coverMotion = useAnimatedStyle(() => {
    'worklet'
    return { opacity: coverOpacity.value }
  })

  return (
    <View
      style={{
        height,
        width: '100%',
        borderRadius: radius,
        overflow: 'hidden',
      }}
    >
      <View
        style={styles.cover}
        pointerEvents={ready ? 'auto' : 'none'}
        accessibilityElementsHidden={!ready}
        importantForAccessibility={ready ? 'auto' : 'no-hide-descendants'}
      >
        {renderSurface !== undefined && (
          <ProviderSurface
            key={surfaceKey}
            renderSurface={renderSurface}
            onReady={() => setReadyFor(surfaceKey)}
            onReadyTimeout={onReadyTimeout}
            readyCeilingMs={readyCeilingMs}
          />
        )}
      </View>
      {ready && uncoveredFor === activeKey ? null : (
        // Keep this cover mounted while preparation hands off to a provider;
        // remounting restarts the spinner delay and its rotation.
        <Animated.View
          style={[
            styles.cover,
            { backgroundColor: theme.background.modal },
            ready ? coverMotion : { opacity: 1 },
          ]}
          pointerEvents={ready ? 'none' : 'auto'}
          accessibilityElementsHidden={ready}
          importantForAccessibility={ready ? 'no-hide-descendants' : 'auto'}
        >
          <ActionLoading label={label} size={size} />
        </Animated.View>
      )}
    </View>
  )
}

/** Readiness and its timeout belong to this provider attempt, not the cover. */
export function ProviderSurface({
  renderSurface,
  onReady,
  onReadyTimeout,
  readyCeilingMs = READY_CEILING_MS,
}: {
  renderSurface: (onReady: () => void) => ReactNode
  onReady: () => void
  onReadyTimeout?: () => void
  readyCeilingMs?: number
}) {
  const active = useRef(true)
  const reported = useRef(false)
  const report = useRef(onReady)
  const timeout = useRef(onReadyTimeout)
  report.current = onReady
  timeout.current = onReadyTimeout
  const markReady = () => {
    if (!active.current || reported.current) {
      return
    }
    reported.current = true
    report.current()
  }
  useLayoutEffect(() => {
    active.current = true
    return () => {
      active.current = false
    }
  }, [])
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!active.current || reported.current) {
        return
      }
      reported.current = true
      if (timeout.current === undefined) {
        report.current()
        return
      }
      timeout.current()
    }, readyCeilingMs)
    return () => clearTimeout(timer)
  }, [readyCeilingMs])
  return renderSurface(markReady)
}

const styles = StyleSheet.create({
  cover: { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 },
})
